import React, { useState, useEffect } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
} from '@mui/material';
import {
  Star as StarIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import premiumUsernameService, { PremiumUsernameDto } from '../../services/premiumUsernameService';
import { getStripePromise } from '../../config/stripe';

interface PremiumUsernameSettingsProps {
  onUsernameChanged?: () => void;
}

const PremiumUsernameSettingsInner: React.FC<PremiumUsernameSettingsProps> = ({ onUsernameChanged }) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [loading, setLoading] = useState(true);
  const [premiumUsername, setPremiumUsername] = useState<PremiumUsernameDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);

  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);

  useEffect(() => {
    fetchPremiumUsername();
  }, []);

  const fetchPremiumUsername = async () => {
    try {
      setLoading(true);
      const premium = await premiumUsernameService.getMyPremiumUsername();
      setPremiumUsername(premium);
    } catch (err: any) {
      console.error('Error fetching premium username:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    if (!stripe || !elements || !premiumUsername) return;

    setRenewLoading(true);
    setError(null);

    try {
      const paymentResponse = await premiumUsernameService.createRenewalPaymentIntent();
      
      if (!paymentResponse.clientSecret) {
        throw new Error('Failed to create payment');
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        paymentResponse.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent?.status === 'succeeded') {
        const renewResult = await premiumUsernameService.renewPremiumUsername(
          premiumUsername.premiumUsernameId,
          paymentIntent.id
        );

        if (renewResult.success) {
          setSuccess('Premium username renewed successfully for another year!');
          setPremiumUsername(renewResult.premiumUsername || null);
          setRenewDialogOpen(false);
          onUsernameChanged?.();
        } else {
          throw new Error(renewResult.error || 'Failed to renew');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to renew premium username');
    } finally {
      setRenewLoading(false);
    }
  };

  const handleSwitchToStandard = async () => {
    setSwitchLoading(true);
    setError(null);

    try {
      const result = await premiumUsernameService.switchToStandardUsername(premiumUsername!.premiumUsernameId);
      
      if (result.success) {
        setSuccess(`Premium username @${premiumUsername?.premiumUsername} has been released. Your account remains active on your existing alias.`);
        setPremiumUsername(null);
        setSwitchDialogOpen(false);
        onUsernameChanged?.();
      } else {
        throw new Error(result.error || 'Failed to release premium username');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to release premium username');
    } finally {
      setSwitchLoading(false);
    }
  };

  const getExpirationStatus = () => {
    if (!premiumUsername) return null;
    const days = premiumUsername.daysUntilExpiration;
    
    if (days <= 0) {
      return { severity: 'error' as const, message: 'Your premium username has expired!' };
    } else if (days <= 7) {
      return { severity: 'error' as const, message: `Expires in ${days} day${days === 1 ? '' : 's'}! Renew now to keep your premium username.` };
    } else if (days <= 15) {
      return { severity: 'warning' as const, message: `Expires in ${days} days. Consider renewing soon.` };
    } else if (days <= 30) {
      return { severity: 'info' as const, message: `Expires in ${days} days.` };
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!premiumUsername) {
    return null;
  }

  const expirationStatus = getExpirationStatus();

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StarIcon color="warning" />
          Premium Username
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Your Premium Username
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            @{premiumUsername.premiumUsername}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip 
            label={premiumUsername.status === 1 ? 'Active' : premiumUsername.status === 2 ? 'Renewed' : 'Expired'}
            color={premiumUsername.status > 0 ? 'success' : 'error'}
            size="small"
          />
          <Chip 
            label={`Expires: ${fmtDate(premiumUsername.expiration)}`}
            variant="outlined"
            size="small"
          />
        </Stack>

        {expirationStatus && (
          <Alert severity={expirationStatus.severity} sx={{ mb: 2 }}>
            {expirationStatus.message}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Premium usernames (all letters, no numbers) require annual renewal. 
          Alternatively, you can switch to a standard username (with 2+ numbers) that never expires.
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => setRenewDialogOpen(true)}
          >
            Renew ($10/year)
          </Button>
          <Button 
            variant="outlined"
            startIcon={<SwapIcon />}
            onClick={() => setSwitchDialogOpen(true)}
          >
            Switch to Standard
          </Button>
        </Stack>
      </CardContent>

      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew Premium Username</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Renew your premium username <strong>@{premiumUsername.premiumUsername}</strong> for another year.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Renewal fee: $10.00
          </Alert>

          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <CardElement 
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': { color: '#aab7c4' },
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewDialogOpen(false)} disabled={renewLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRenew}
            disabled={renewLoading || !stripe}
            startIcon={renewLoading ? <CircularProgress size={20} /> : null}
          >
            {renewLoading ? 'Processing...' : 'Pay $10 & Renew'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={switchDialogOpen} onClose={() => !switchLoading && setSwitchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Release Premium Username</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You are about to release your premium username <strong>@{premiumUsername.premiumUsername}</strong>.
              This name will become available for others to claim.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary">
            All messages and receipts sent under <strong>@{premiumUsername.premiumUsername}</strong> remain
            visible in your account history. No action is needed — your existing account alias continues
            to work normally after the premium name is released.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwitchDialogOpen(false)} disabled={switchLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSwitchToStandard}
            disabled={switchLoading}
            startIcon={switchLoading ? <CircularProgress size={20} /> : <SwapIcon />}
          >
            {switchLoading ? 'Releasing...' : 'Release Premium Name'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const PremiumUsernameSettings: React.FC<PremiumUsernameSettingsProps> = (props) => {
  return (
    <Elements stripe={getStripePromise()}>
      <PremiumUsernameSettingsInner {...props} />
    </Elements>
  );
};

export default PremiumUsernameSettings;

